import type { LoanTypeType } from '../dtos/term-sheet.dto';

// ============================================================================
// DOCUMENT REQUIREMENT TYPES
// ============================================================================

/**
 * Document category for grouping related documents
 */
export type DocumentCategory = 'personal' | 'property' | 'financial' | 'legal';

/**
 * Document requirement definition
 */
export interface DocumentRequirement {
  id: string;
  name: string;
  category: DocumentCategory;
  description: string;
  acceptsTypes: string[];
  requiredForLoanTypes: LoanTypeType[];
  optionalForLoanTypes?: LoanTypeType[];
}

// ============================================================================
// DOCUMENT REQUIREMENTS MATRIX
// ============================================================================

/**
 * Complete document requirements matrix
 * Maps document types to loan types where they are required or optional
 */
export const DOCUMENT_REQUIREMENTS: DocumentRequirement[] = [
  {
    id: 'drivers_license',
    name: 'Government-Issued Photo ID',
    category: 'personal',
    description: "Valid government-issued photo identification (driver's license, passport, or state ID)",
    acceptsTypes: ['drivers_license', 'passport', 'state_id'],
    requiredForLoanTypes: ['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily'],
  },
  {
    id: 'utility_bill',
    name: 'Proof of Address',
    category: 'personal',
    description: 'Recent utility bill or similar document showing current address',
    acceptsTypes: ['utility_bill'],
    requiredForLoanTypes: ['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily'],
  },
  {
    id: 'tax_return_personal',
    name: 'Personal Tax Returns (Last 2 Years)',
    category: 'financial',
    description: 'Complete personal tax returns including all schedules',
    acceptsTypes: ['tax_return_personal'],
    requiredForLoanTypes: ['fix_and_flip', 'bridge', 'ground_up', 'commercial', 'multifamily'],
    optionalForLoanTypes: ['dscr'],
  },
  {
    id: 'tax_return_business',
    name: 'Business Tax Returns (Last 2 Years)',
    category: 'financial',
    description: 'Business tax returns for operating entity',
    acceptsTypes: ['tax_return_business'],
    requiredForLoanTypes: ['commercial', 'multifamily'],
    optionalForLoanTypes: ['fix_and_flip', 'bridge', 'ground_up'],
  },
  {
    id: 'bank_statement_personal',
    name: 'Bank Statements (Last 3 Months)',
    category: 'financial',
    description: 'Most recent 3 months of bank statements for all accounts',
    acceptsTypes: ['bank_statement_personal', 'bank_statement_business'],
    requiredForLoanTypes: ['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily'],
  },
  {
    id: 'operating_agreement',
    name: 'Entity/Organization Documents',
    category: 'legal',
    description: 'Operating agreement, articles of incorporation, or certificate of good standing',
    acceptsTypes: ['operating_agreement', 'articles_of_incorporation', 'good_standing_certificate'],
    requiredForLoanTypes: ['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily'],
  },
  {
    id: 'purchase_agreement',
    name: 'Purchase Contract/Agreement',
    category: 'property',
    description: 'Fully executed purchase and sale agreement',
    acceptsTypes: ['purchase_agreement'],
    requiredForLoanTypes: ['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily'],
  },
  {
    id: 'property_insurance',
    name: 'Property Insurance',
    category: 'property',
    description: 'Evidence of property insurance or binder',
    acceptsTypes: ['property_insurance'],
    requiredForLoanTypes: ['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily'],
  },
  {
    id: 'property_appraisal',
    name: 'Property Appraisal',
    category: 'property',
    description: 'Current property appraisal from a licensed appraiser',
    acceptsTypes: ['property_appraisal'],
    requiredForLoanTypes: ['bridge', 'dscr', 'commercial', 'multifamily'],
    optionalForLoanTypes: ['fix_and_flip', 'ground_up'],
  },
  {
    id: 'scope_of_work',
    name: 'Renovation Budget / Scope of Work',
    category: 'property',
    description: 'Detailed renovation budget or scope of work',
    acceptsTypes: ['scope_of_work', 'construction_budget'],
    requiredForLoanTypes: ['fix_and_flip', 'ground_up'],
    optionalForLoanTypes: ['bridge'],
  },
  {
    id: 'contractor_license',
    name: 'Contractor License',
    category: 'legal',
    description: 'Valid contractor license for renovation/construction work',
    acceptsTypes: ['contractor_license'],
    requiredForLoanTypes: ['fix_and_flip', 'ground_up'],
  },
  {
    id: 'construction_plans',
    name: 'Construction Plans',
    category: 'property',
    description: 'Architectural plans and construction drawings',
    acceptsTypes: ['construction_plans'],
    requiredForLoanTypes: ['ground_up'],
  },
  {
    id: 'building_permits',
    name: 'Building Permits',
    category: 'legal',
    description: 'Required building permits and approvals',
    acceptsTypes: ['building_permits'],
    requiredForLoanTypes: ['ground_up'],
    optionalForLoanTypes: ['fix_and_flip'],
  },
  {
    id: 'rent_roll',
    name: 'Rent Roll',
    category: 'financial',
    description: 'Current rent roll showing all units and lease terms',
    acceptsTypes: ['rent_roll'],
    requiredForLoanTypes: ['dscr', 'commercial', 'multifamily'],
  },
  {
    id: 'lease_agreements',
    name: 'Lease Agreements',
    category: 'legal',
    description: 'Copies of all current lease agreements',
    acceptsTypes: ['lease_agreements'],
    requiredForLoanTypes: ['dscr', 'commercial', 'multifamily'],
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get document requirements for a specific loan type
 * @param loanType - The loan type to get requirements for
 * @returns Object containing required and optional documents
 */
export function getDocumentsForLoanType(loanType: LoanTypeType): {
  required: DocumentRequirement[];
  optional: DocumentRequirement[];
} {
  const required = DOCUMENT_REQUIREMENTS.filter((doc) => doc.requiredForLoanTypes.includes(loanType));

  const optional = DOCUMENT_REQUIREMENTS.filter((doc) => doc.optionalForLoanTypes?.includes(loanType));

  return { required, optional };
}

/**
 * Group documents by category
 * @param documents - Array of document requirements
 * @returns Object with documents grouped by category
 */
export function groupDocumentsByCategory(
  documents: DocumentRequirement[],
): Record<DocumentCategory, DocumentRequirement[]> {
  return documents.reduce(
    (acc, doc) => {
      if (!acc[doc.category]) {
        acc[doc.category] = [];
      }
      acc[doc.category].push(doc);
      return acc;
    },
    {
      personal: [],
      property: [],
      financial: [],
      legal: [],
    } as Record<DocumentCategory, DocumentRequirement[]>,
  );
}

/**
 * Get all unique document categories
 */
export const DOCUMENT_CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'personal', label: 'Personal Documents' },
  { value: 'property', label: 'Property Documents' },
  { value: 'financial', label: 'Financial Documents' },
  { value: 'legal', label: 'Legal Documents' },
];
